<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
interface FeedbackItem {
  id?: string | number
  name: string
  avatar?: string
  country?: string
  time?: string
  submittedAt?: string
  content: string
  detailUrl?: string
}

const props = withDefaults(
  defineProps<{
    hasSiteVisit?: boolean
    feedbackItems?: FeedbackItem[]
    feedbackCount?: number
    showFeedbackCount?: boolean
    exchangeName?: string
    exchangeId?: string
  }>(),
  {
    hasSiteVisit: true,
    feedbackItems: () => [],
    showFeedbackCount: true,
    exchangeName: '',
    exchangeId: '',
  },
)

const emit = defineEmits<{
  revealFeedback: []
}>()

const { localePath, t } = useAppI18n()
const { isRtl } = useRtl()
const matomo = useMatomo()
const nuxtLinkComponent = resolveComponent('NuxtLink')

const normalizedFeedbackItems = computed(() =>
  props.feedbackItems.filter((item) => item.name.trim() && item.content.trim()),
)
const actualFeedbackCount = computed(() => Math.max(props.feedbackCount ?? 0, normalizedFeedbackItems.value.length))
const formattedFeedbackCount = computed(() =>
  actualFeedbackCount.value > 999 ? '999+' : String(actualFeedbackCount.value),
)
const isSingleDigitFeedbackCount = computed(() => actualFeedbackCount.value >= 0 && actualFeedbackCount.value < 10)
const feedbackDisplayLimit = computed(() => {
  if (!props.hasSiteVisit) {
    return 4
  }

  return actualFeedbackCount.value >= 2 ? 2 : 1
})
const visibleFeedbackItems = computed(() => normalizedFeedbackItems.value.slice(0, feedbackDisplayLimit.value))
const getFeedbackMetaText = (item: FeedbackItem) =>
  [item.country, item.submittedAt || item.time].filter(Boolean).join(' · ')

// 有详情地址的反馈项使用 NuxtLink，无地址时保留原有静态卡片展示。
const getFeedbackDetailPath = (item: FeedbackItem) => {
  if (!item.id && !item.detailUrl) {
    return ''
  }

  return (
    item.detailUrl ||
    localePath({
      name: 'feedback-detail',
      params: { review_id: item.id },
    })
  )
}

// 动态组件只在存在详情地址时注入链接属性，避免静态 article 带无效属性。
const getFeedbackLinkProps = (item: FeedbackItem) => {
  const detailUrl = getFeedbackDetailPath(item)

  if (!detailUrl) {
    return {}
  }

  return {
    to: detailUrl,
    external: /^https?:\/\//.test(detailUrl),
    target: '_blank',
    rel: 'noopener noreferrer',
  }
}

// NuxtLink 负责跳转，这里仅保留原有反馈详情埋点。
const trackFeedbackDetailClick = (item: FeedbackItem) => {
  matomo.trackEvent('broker_feedback_click', {
    af_type: '详情',
    af_name: props.exchangeId || '',
    af_code: props.exchangeName || '',
  })
}

const getScrollMarginTop = (target: Element) => {
  const scrollMarginTop = window.getComputedStyle(target).scrollMarginTop
  const offset = Number.parseFloat(scrollMarginTop)

  return Number.isFinite(offset) ? offset : 0
}

const scrollToTopSmoothly = (top: number) => {
  window.scrollTo({
    top,
    behavior: 'smooth',
  })
}

const scrollToComments = () => {
  matomo.trackEvent('broker_feedback_click', {
    af_type: '更多',
    af_name: props.exchangeId || '',
    af_code: props.exchangeName || '',
  })
  emit('revealFeedback')

  const target =
    document.querySelector('#exchange-feedback-tab') ||
    document.querySelector('#comments-section') ||
    document.querySelector('#comments') ||
    document.querySelector('.comments-area') ||
    document.querySelector('.exchange-comments')

  if (target) {
    scrollToTopSmoothly(Math.max(target.getBoundingClientRect().top + window.scrollY - getScrollMarginTop(target), 0))
  } else {
    scrollToTopSmoothly(document.body.scrollHeight)
  }
}
</script>

<template>
  <section class="feedback-card" :class="{ 'feedback-card-full': !hasSiteVisit }">
    <div class="feedback-heading">
      <h2>{{ t('83846') }}</h2>
      <div class="feedback-heading-action" @click="scrollToComments">
        <span
          v-if="showFeedbackCount"
          class="feedback-count"
          :class="{ 'feedback-count-single': isSingleDigitFeedbackCount }"
        >
          {{ formattedFeedbackCount }}
        </span>
        <ArrowRight class="feedback-arrow" :class="{ 'feedback-arrow-rtl': isRtl }" aria-hidden="true" />
      </div>
    </div>

    <div
      class="feedback-list"
      :class="{ 'feedback-list-full': !hasSiteVisit }"
      :style="!hasSiteVisit ? { '--feedback-column-count': visibleFeedbackItems.length } : undefined"
    >
      <component
        :is="getFeedbackDetailPath(item) ? nuxtLinkComponent : 'article'"
        v-for="item in visibleFeedbackItems"
        :key="item.id || item.name"
        class="feedback-item"
        v-bind="getFeedbackLinkProps(item)"
        @click="trackFeedbackDetailClick(item)"
      >
        <div class="feedback-author">
          <CommonImage v-if="item.avatar" :src="item.avatar" class="feedback-avatar" alt="" loading="eager" />
          <span v-else class="feedback-avatar feedback-avatar-fallback" aria-hidden="true">
            {{ item.name.slice(0, 1) }}
          </span>
          <div class="author-info">
            <h3>{{ item.name }}</h3>
            <p v-if="getFeedbackMetaText(item)">{{ getFeedbackMetaText(item) }}</p>
          </div>
        </div>
        <p class="feedback-content">{{ item.content }}</p>
      </component>
    </div>
  </section>
</template>

<style scoped lang="less">
.feedback-card {
  --color-text: #11151c;
  --color-border-default: rgba(5, 21, 36, 0.06);
  --color-bg-hover: rgba(5, 21, 36, 0.04);

  width: 100%;
  padding-inline-start: 32px;
}

.feedback-card-full {
  padding-inline-start: 0;
}

.feedback-heading {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding-inline-end: 60px;
  height: 26px;
}

.feedback-heading h2 {
  margin: 0;
  color: #292a2e;
  /* Title/L-CN-18-600 */
  font-size: var(--Font-Size--L-18, 18px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--Line-Height--L-18, 26px);
}

.feedback-count {
  display: inline-flex;
  height: 20px;
  min-width: 20px;
  align-items: center;
  justify-content: center;
  color: #7d818a;
  font-size: 14px;
  font-weight: 400;
  background: #f8f8f8;
  padding: 0 4px;
  border-radius: 1000px;
  line-height: 20px;
}

.feedback-count-single {
  width: 20px;
  padding: 0;
}

.feedback-heading-action {
  position: absolute;
  // top: 0;
  inset-inline-end: 0;
  cursor: pointer;
  display: flex;
  justify-content: end;
  align-items: center;
}

.feedback-card:dir(rtl) .feedback-heading-action {
  inset-inline-end: 6px;
}

.more-btn {
  cursor: pointer;

  &:hover {
    color: #292a2e;
  }

  &:focus-visible {
    color: #292a2e;
    box-shadow: 0 0 0 2px rgba(41, 42, 46, 0.18);
  }
}

.feedback-arrow {
  width: 20px;
  height: 20px;
  &:hover {
    color: #292a2e;
  }
}

.feedback-arrow-rtl {
  transform: rotate(180deg);
}

.feedback-arrow :deep(path) {
  fill: currentColor;
}

.feedback-heading-action:hover .feedback-arrow::before {
  border-color: #292a2e;
}

.feedback-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 20px;
  row-gap: 24px;
  margin-top: 20px;
}

.feedback-list-full {
  grid-template-columns: repeat(var(--feedback-column-count, 4), minmax(0, 1fr));
  column-gap: 96px;

  .feedback-item {
    position: relative;

    & + .feedback-item::before {
      position: absolute;
      top: 32px;
      bottom: 8px;
      inset-inline-start: -40px;
      width: 1px;
      background: var(--color-border-default, rgba(5, 21, 36, 0.06));
      content: '';
      pointer-events: none;
    }
  }
}

.feedback-item {
  min-width: 0;
  border-radius: 8px;
  color: inherit;
  cursor: pointer;
  margin: -8px;
  padding: 8px;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover,
  &:focus-visible {
    background-color: #f8f8f8;

    .feedback-content,
    h3 {
      color: #292a2e;
    }
  }
}

.feedback-author {
  display: flex;
  align-items: center;
  gap: 12px;
}

.feedback-avatar {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  border-radius: 40px;
  border: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  background-color: #f3f1eb;
  flex-shrink: 0;
  object-fit: cover;
}

.feedback-avatar-fallback {
  color: #292a2e;
  font-size: 16px;
  font-weight: 600;
}

.author-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.feedback-author h3 {
  margin: 0;
  color: #292a2e;
  /* Title/M-CN-16-600 */
  font-size: var(--Font-Size--M-16, 16px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--Line-Height--M-16, 24px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feedback-author p {
  margin: 4px 0 0;
  color: #9ba0ae;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}

.feedback-content {
  min-height: 66px;
  padding-top: 12px;
  display: -webkit-box;
  overflow: hidden;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  word-break: break-word;
}

@media screen and (max-width: 1180px) {
  .feedback-card {
    padding-inline-start: 0;
  }

  .feedback-list-full {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .feedback-item:nth-child(odd)::before {
      display: none;
    }
  }
}

@media screen and (max-width: 720px) {
  .feedback-list,
  .feedback-list-full {
    grid-template-columns: 1fr;
    row-gap: 20px;

    .feedback-item::before {
      display: none;
    }
  }
}
</style>
